/**
 * PORTED — RO-3-00c / #44470.
 * Origin: `excellent/app/_modes/objects-table.tsx`.
 *
 * Changes:
 *   `"use client"`                 → dropped (inert in this host)
 *   `next/link`                    → `RouterLink` from `@excellent/ui/router`
 *   `useParams` from next/nav      → dropped (mode comes from prop/`from`)
 *   `./focus-on-hash`              → local ported copy
 */

import {
  Button,
  CommitmentTable,
  useOpenObject,
  type CommitmentTableRow,
  type EmptyStateProps,
} from "@excellent/ui";
import { Boxes } from "@excellent/ui/icons";
import { RouterLink } from "@excellent/ui/router";
import { modePath, type ModeSlug } from "@/modes/modes-registry";
import { FocusOnHash } from "./focus-on-hash";

/**
 * The mode Objects view's table (UI-rewrite P5). Wraps CommitmentTable so every
 * row opens its object peek. Prop-driven: `rows` arrive from `modes.data` via
 * the ModePage rewrite (no vault reach here).
 */
export function ObjectsTable({
  rows,
  from,
  emptyState,
}: {
  rows: CommitmentTableRow[];
  from?: string;
  emptyState?: Omit<EmptyStateProps, "variant" | "colSpan">;
}) {
  const openObject = useOpenObject();
  const queueHref = from
    ? modePath(from as ModeSlug, { view: "queue" })
    : "/work?view=queue";

  return (
    <>
      <FocusOnHash />
      <CommitmentTable
        rows={rows}
        onRowClick={(row) => openObject(row.uid)}
        emptyState={
          emptyState ?? {
            icon: <Boxes className="size-5" />,
            title: "No objects yet",
            description:
              "Objects in this mode appear here as commitments land — open the Queue to act on what's ready.",
            action: (
              <Button asChild size="sm" variant="outline">
                <RouterLink href={queueHref}>Go to Queue</RouterLink>
              </Button>
            ),
          }
        }
      />
    </>
  );
}
